if (!customElements.get('kg-blend-builder')) {
  customElements.define(
    'kg-blend-builder',
    class KgBlendBuilder extends HTMLElement {
      constructor() {
        super();
        this.currentStep = 1;
        this.selectedProteinKeys = new Set();
        this.selectedIngredientKeys = new Set();
        this.selectedPremixKeys = '';
        this.cameFromPremixes = false;
        this.tileMap = new Map();
      }

      connectedCallback() {
        this.cacheElements();
        this.indexTiles();
        this.bindEvents();
        this.initAccordions();
        const startOnPremixes = this.shouldStartOnPremixes();
        this.cameFromPremixes = startOnPremixes;
        this.goToStep(startOnPremixes ? 'premixes' : 1);
        this.updateUI();
      }

      shouldStartOnPremixes() {
        const params = new URLSearchParams(window.location.search);
        return window.location.hash === '#premixes' || params.get('step') === 'premixes';
      }

      cacheElements() {
        this.steps = this.querySelectorAll('[data-step]');
        this.proteinTiles = this.querySelectorAll('[data-item-type="protein"]');
        this.ingredientTiles = this.querySelectorAll('[data-item-type="ingredient"]');
        this.premixButtons = this.querySelectorAll('[data-premix-keys]');
        this.categoryNavButtons = this.querySelectorAll('[data-category-nav]');
        this.premixesToggle = this.querySelector('[data-action="show-premixes"]');
        this.jar = this.querySelector('.kg-blend-builder__jar');
        this.jarTags = this.querySelector('[data-jar-tags]');
        this.jarCount = this.querySelector('[data-jar-count]');
        this.blendNameInput = this.querySelector('[data-blend-name]');
        this.statusMessage = this.querySelector('[data-status-message]');
        this.backButton = this.querySelector('.kg-blend-builder__actions [data-action="prev-step"]');
        this.primaryActionButton = this.querySelector('[data-action-primary]');
        this.nextStepButtons = this.querySelectorAll('[data-action="next-step"]');
        this.addToCartButtons = this.querySelectorAll('.kg-blend-builder__checkout [data-action="add-to-cart"]');
        this.progressSteps = this.querySelectorAll('[data-progress-step]');
        this.categoryBadges = this.querySelectorAll('[data-category-badge]');
        this.priceDisplay = this.querySelector('[data-price-display]');
        this.sellingPlanSelect = this.querySelector('[data-selling-plan-select]');
        this.previewName = this.querySelector('[data-preview-name]');
        this.previewProtein = this.querySelector('[data-preview-protein]');
        this.previewCalories = this.querySelector('[data-preview-calories]');
        this.previewCarbs = this.querySelector('[data-preview-carbs]');
        this.previewFat = this.querySelector('[data-preview-fat]');
        this.previewCount = this.querySelector('[data-preview-count]');
        this.previewList = this.querySelector('[data-preview-list]');
        this.defaultBlendName = 'Custom Blend';
        this.jarEmptyText = this.jar?.dataset.jarEmptyText || 'Add ingredients to your blend';
        this.jarServingLabel = this.jar?.dataset.jarServing || '1 Scoop (30g)';
        this.ingredientsLabel = this.jarCount?.textContent.replace(/^\d+\s*/, '').trim() || 'ingredients added';
      }

      indexTiles() {
        this.querySelectorAll('[data-item-key]').forEach((tile) => {
          this.tileMap.set(tile.dataset.itemKey, tile);
        });
      }

      bindEvents() {
        this.addEventListener('click', this.onClick.bind(this));

        this.proteinTiles.forEach((tile) => {
          tile.addEventListener('click', () => this.toggleProtein(tile.dataset.itemKey));
        });

        this.ingredientTiles.forEach((tile) => {
          tile.addEventListener('click', () => this.toggleIngredient(tile.dataset.itemKey));
        });

        this.premixButtons.forEach((button) => {
          button.addEventListener('click', () => this.applyPremix(button, { goToReview: true }));
        });

        this.categoryNavButtons.forEach((button) => {
          button.addEventListener('click', () => this.navigateToCategory(button.dataset.categoryNav, button));
        });

        this.progressSteps.forEach((progressStep) => {
          progressStep.addEventListener('click', () => {
            const targetStep = Number(progressStep.dataset.progressStep);
            if (!targetStep) return;

            const hasSelection = this.getSelectedItems().length > 0;
            if (targetStep === 4) {
              if (!hasSelection) return;
            } else if (targetStep > 1 && this.selectedProteinKeys.size === 0 && !this.selectedPremixKeys) {
              return;
            }

            this.cameFromPremixes = false;
            this.goToStep(targetStep);
          });
        });

        if (this.blendNameInput) {
          this.blendNameInput.addEventListener('input', () => this.updateUI());
        }

        if (this.sellingPlanSelect) {
          this.sellingPlanSelect.addEventListener('change', () => this.updateSellingPlanPrice());
        }
      }

      getStepForCategory(category) {
        if (category === 'proteins') return 1;
        if (category === 'flavors') return 3;
        return 2;
      }

      setActiveCategoryPill(activeButton) {
        this.categoryNavButtons.forEach((button) => button.classList.remove('is-active'));
        this.premixesToggle?.classList.remove('is-active');
        activeButton?.classList.add('is-active');
      }

      navigateToCategory(category, button) {
        if (!category) return;

        this.cameFromPremixes = false;
        this.setActiveCategoryPill(button);

        const step = this.getStepForCategory(category);
        this.goToStep(step, { syncPills: false });

        if (step === 2) {
          requestAnimationFrame(() => {
            const accordion = this.querySelector(`[data-accordion][data-category="${category}"]`);
            if (!accordion) return;

            this.accordions?.forEach((item) => {
              if (item !== accordion) this.closeAccordion(item);
            });

            if (!accordion.classList.contains('is-open')) {
              this.openAccordion(accordion);
            } else {
              this.setActiveCategoryPill(button);
            }
          });
        }
      }

      updateSellingPlanPrice() {
        if (!this.priceDisplay || !this.sellingPlanSelect) return;

        const selectedOption = this.sellingPlanSelect.options[this.sellingPlanSelect.selectedIndex];
        const formattedPrice = selectedOption?.dataset.price;

        if (formattedPrice) {
          this.priceDisplay.textContent = formattedPrice;
        }
      }

      initAccordions() {
        if (this._accordionsReady) return;
        this._accordionsReady = true;

        this.accordions = this.querySelectorAll('[data-accordion]');

        this.accordions.forEach((accordion) => {
          const trigger = accordion.querySelector('[data-accordion-trigger]');
          const panel = accordion.querySelector('[data-accordion-panel]');
          if (!trigger || !panel) return;

          if (accordion.classList.contains('is-open')) {
            panel.hidden = false;
            panel.style.height = 'auto';
          }

          trigger.addEventListener('click', () => {
            const isOpen = accordion.classList.contains('is-open');
            if (isOpen) {
              this.closeAccordion(accordion);
              return;
            }

            this.accordions.forEach((item) => {
              if (item !== accordion) this.closeAccordion(item);
            });
            this.openAccordion(accordion);

            if (String(this.currentStep) === '2' && accordion.dataset.category) {
              const pill = this.querySelector(`[data-category-nav="${accordion.dataset.category}"]`);
              if (pill) this.setActiveCategoryPill(pill);
            }
          });
        });
      }

      openAccordion(accordion) {
        const trigger = accordion.querySelector('[data-accordion-trigger]');
        const panel = accordion.querySelector('[data-accordion-panel]');
        if (!trigger || !panel || accordion.classList.contains('is-open')) return;

        accordion.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        panel.style.height = '0px';
        panel.offsetHeight;
        panel.style.height = `${panel.scrollHeight}px`;

        const onOpenEnd = (event) => {
          if (event.propertyName !== 'height') return;
          if (accordion.classList.contains('is-open')) {
            panel.style.height = 'auto';
          }
          panel.removeEventListener('transitionend', onOpenEnd);
        };

        panel.addEventListener('transitionend', onOpenEnd);
      }

      closeAccordion(accordion) {
        const trigger = accordion.querySelector('[data-accordion-trigger]');
        const panel = accordion.querySelector('[data-accordion-panel]');
        if (!trigger || !panel || !accordion.classList.contains('is-open')) return;

        accordion.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        panel.style.height = `${panel.scrollHeight}px`;
        panel.offsetHeight;
        panel.style.height = '0px';

        const onCloseEnd = (event) => {
          if (event.propertyName !== 'height') return;
          if (!accordion.classList.contains('is-open')) {
            panel.hidden = true;
            panel.style.height = '';
          }
          panel.removeEventListener('transitionend', onCloseEnd);
        };

        panel.addEventListener('transitionend', onCloseEnd);
      }

      onClick(event) {
        const removeTag = event.target.closest('[data-remove-key]');
        if (removeTag) {
          event.preventDefault();
          const key = removeTag.dataset.removeKey;
          if (this.selectedProteinKeys.has(key)) {
            this.selectedProteinKeys.delete(key);
          } else {
            this.selectedIngredientKeys.delete(key);
          }
          this.clearPremixSelection();
          this.syncTileStates();
          this.updateUI();
          return;
        }

        const action = event.target.closest('[data-action]');
        if (!action) return;

        const actionName = action.dataset.action;

        if (actionName === 'next-step') {
          const targetStep = Number(action.dataset.targetStep);
          if (targetStep === 2 && this.selectedProteinKeys.size === 0) return;
          if (action.hasAttribute('data-require-premix') && !this.selectedPremixKeys) return;
          this.cameFromPremixes = String(this.currentStep) === 'premixes' || Boolean(this.selectedPremixKeys);
          this.goToStep(targetStep);
        }

        if (actionName === 'review-blend') {
          const items = this.getSelectedItems();
          if (items.length === 0) return;
          this.cameFromPremixes = String(this.currentStep) === 'premixes' || Boolean(this.selectedPremixKeys);
          this.goToStep(4);
        }

        if (actionName === 'prev-step') {
          if (String(this.currentStep) === '1') return;
          const targetStep = action.dataset.targetStep;
          this.cameFromPremixes = false;
          this.goToStep(targetStep === 'premixes' ? 'premixes' : Number(targetStep));
        }

        if (actionName === 'add-to-cart') {
          this.addToCart(action);
        }

        if (actionName === 'clear-blend') {
          this.selectedProteinKeys.clear();
          this.selectedIngredientKeys.clear();
          this.clearPremixSelection();
          this.cameFromPremixes = false;
          this.syncTileStates();
          this.updateUI();
        }

        if (actionName === 'show-premixes') {
          this.cameFromPremixes = true;
          this.goToStep('premixes');
        }
      }

      clearPremixSelection() {
        this.selectedPremixKeys = '';
        this.premixButtons.forEach((tile) => {
          tile.classList.remove('is-selected', 'is-active');
          tile.setAttribute('aria-pressed', 'false');
        });
      }

      applyPremix(button, { goToReview = false } = {}) {
        const keys = button.dataset.premixKeys
          .split(',')
          .map((key) => key.trim())
          .filter(Boolean);

        const proteinKeys = keys.filter((key) => {
          const tile = this.tileMap.get(key);
          return tile && tile.dataset.itemType === 'protein';
        });

        // Fallback for older premix markup that only exposes data-premix-protein
        const legacyProtein = button.dataset.premixProtein;
        if (proteinKeys.length === 0 && legacyProtein && this.tileMap.has(legacyProtein)) {
          proteinKeys.push(legacyProtein);
        }

        this.selectedProteinKeys = new Set(proteinKeys);
        this.selectedIngredientKeys = new Set(
          keys.filter((key) => {
            const tile = this.tileMap.get(key);
            return tile && tile.dataset.itemType === 'ingredient';
          })
        );
        this.selectedPremixKeys = button.dataset.premixKeys || '';
        this.cameFromPremixes = true;

        this.premixButtons.forEach((tile) => {
          const selected = tile === button;
          tile.classList.toggle('is-selected', selected);
          tile.classList.toggle('is-active', selected);
          tile.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        this.syncTileStates();
        this.updateUI();

        if (goToReview) {
          this.goToStep(4);
        }
      }

      toggleProtein(key) {
        if (!key) return;

        if (this.selectedProteinKeys.has(key)) {
          this.selectedProteinKeys.delete(key);
        } else {
          this.selectedProteinKeys.add(key);
        }

        this.clearPremixSelection();
        this.cameFromPremixes = false;
        this.syncTileStates();
        this.updateUI();
      }

      toggleIngredient(key) {
        if (this.selectedIngredientKeys.has(key)) {
          this.selectedIngredientKeys.delete(key);
        } else {
          this.selectedIngredientKeys.add(key);
        }
        this.clearPremixSelection();
        this.cameFromPremixes = false;
        this.syncTileStates();
        this.updateUI();
      }

      syncTileStates() {
        this.proteinTiles.forEach((tile) => {
          const selected = this.selectedProteinKeys.has(tile.dataset.itemKey);
          tile.classList.toggle('is-selected', selected);
          tile.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        this.ingredientTiles.forEach((tile) => {
          const selected = this.selectedIngredientKeys.has(tile.dataset.itemKey);
          tile.classList.toggle('is-selected', selected);
          tile.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
      }

      getSelectedItems() {
        const items = [];

        this.selectedProteinKeys.forEach((key) => {
          const tile = this.tileMap.get(key);
          if (tile) items.push(tile);
        });

        this.selectedIngredientKeys.forEach((key) => {
          const tile = this.tileMap.get(key);
          if (tile) items.push(tile);
        });

        return items;
      }

      getSelectedProteinTitles() {
        return Array.from(this.selectedProteinKeys)
          .map((key) => this.tileMap.get(key)?.dataset.itemTitle)
          .filter(Boolean)
          .join(', ');
      }

      getNutritionSummary() {
        const read = (selector) => this.querySelector(selector)?.textContent?.trim() || '';
        const protein = read('[data-nutrition-summary-protein]') || '0g';
        const calories = read('[data-nutrition-summary-calories]') || '0';
        const carbs = read('[data-nutrition-summary-carbs]') || '0g';
        const fat = read('[data-nutrition-summary-fat]') || '0g';

        return `${protein} Protein, ${calories} Calories, ${carbs} Carbs, ${fat} Fat`;
      }

      nutrientNumber(value) {
        const amount = Number(value);
        return Number.isFinite(amount) ? amount : 0;
      }

      formatNutrient(value) {
        const rounded = Math.round(this.nutrientNumber(value) * 10) / 10;
        if (rounded === 0) return '0';
        if (Number.isInteger(rounded)) return String(rounded);
        return rounded.toFixed(1);
      }

      getNutritionFromTile(tile) {
        return {
          calories: this.nutrientNumber(tile.dataset.calories),
          totalFat: this.nutrientNumber(tile.dataset.totalFat),
          saturatedFat: this.nutrientNumber(tile.dataset.saturatedFat),
          transFat: this.nutrientNumber(tile.dataset.transFat),
          cholesterol: this.nutrientNumber(tile.dataset.cholesterol),
          sodium: this.nutrientNumber(tile.dataset.sodium),
          totalCarbs: this.nutrientNumber(tile.dataset.totalCarbs),
          dietaryFiber: this.nutrientNumber(tile.dataset.dietaryFiber),
          totalSugars: this.nutrientNumber(tile.dataset.totalSugars),
          addedSugars: this.nutrientNumber(tile.dataset.addedSugars),
          protein: this.nutrientNumber(tile.dataset.protein),
          vitaminD: this.nutrientNumber(tile.dataset.vitaminD),
          calcium: this.nutrientNumber(tile.dataset.calcium),
          iron: this.nutrientNumber(tile.dataset.iron),
          potassium: this.nutrientNumber(tile.dataset.potassium),
        };
      }

      sumMacroNutrition(items) {
        return items.reduce(
          (totals, tile) => {
            const values = this.getNutritionFromTile(tile);
            Object.keys(totals).forEach((key) => {
              totals[key] += values[key];
            });
            return totals;
          },
          {
            calories: 0,
            totalFat: 0,
            saturatedFat: 0,
            transFat: 0,
            cholesterol: 0,
            sodium: 0,
            totalCarbs: 0,
            dietaryFiber: 0,
            totalSugars: 0,
            addedSugars: 0,
            protein: 0,
            vitaminD: 0,
            calcium: 0,
            iron: 0,
            potassium: 0,
          }
        );
      }

      appendLabelNutrition(formData, totals) {
        const gram = (value) => `${this.formatNutrient(value)}g`;
        const milligram = (value) => `${this.formatNutrient(value)}mg`;
        const facts = [
          ['_Calories', this.formatNutrient(totals.calories)],
          ['_Total Fat', gram(totals.totalFat)],
          ['_Total Fat DV', this.dailyValue(totals.totalFat, 78)],
          ['_Saturated Fat', gram(totals.saturatedFat)],
          ['_Saturated Fat DV', this.dailyValue(totals.saturatedFat, 20)],
          ['_Trans Fat', gram(totals.transFat)],
          ['_Cholesterol', milligram(totals.cholesterol)],
          ['_Cholesterol DV', this.dailyValue(totals.cholesterol, 300)],
          ['_Sodium', milligram(totals.sodium)],
          ['_Sodium DV', this.dailyValue(totals.sodium, 2300)],
          ['_Total Carbohydrate', gram(totals.totalCarbs)],
          ['_Total Carbohydrate DV', this.dailyValue(totals.totalCarbs, 275)],
          ['_Dietary Fiber', gram(totals.dietaryFiber)],
          ['_Dietary Fiber DV', this.dailyValue(totals.dietaryFiber, 28)],
          ['_Total Sugars', gram(totals.totalSugars)],
          ['_Added Sugars', gram(totals.addedSugars)],
          ['_Added Sugars DV', this.dailyValue(totals.addedSugars, 50)],
          ['_Protein', gram(totals.protein)],
          ['_Vitamin D', `${this.formatNutrient(totals.vitaminD)}mcg`],
          ['_Vitamin D DV', this.dailyValue(totals.vitaminD, 20)],
          ['_Calcium', milligram(totals.calcium)],
          ['_Calcium DV', this.dailyValue(totals.calcium, 1300)],
          ['_Iron', milligram(totals.iron)],
          ['_Iron DV', this.dailyValue(totals.iron, 18)],
          ['_Potassium', milligram(totals.potassium)],
          ['_Potassium DV', this.dailyValue(totals.potassium, 4700)],
        ];

        facts.forEach(([name, value]) => {
          formData.append(`properties[${name}]`, value);
        });
      }

      getBaselineNutrition() {
        const sodium = Number(this.dataset.baseSodium) || 120;
        const calcium = Number(this.dataset.baseCalcium) || 260;
        const iron = Number(this.dataset.baseIron) || 8;
        const potassium = Number(this.dataset.basePotassium) || 235;

        return {
          cholesterol: 0,
          sodium,
          vitaminD: 0,
          calcium,
          iron,
          potassium,
          sodiumDv: '5%',
          calciumDv: '20%',
          ironDv: '45%',
          potassiumDv: '6%',
        };
      }

      dailyValue(amount, daily) {
        return `${Math.round((amount / daily) * 100)}%`;
      }

      updateNutritionPanel(items) {
        const macros = this.sumMacroNutrition(items);
        const baseline = this.getBaselineNutrition();
        const setText = (selector, value) => {
          const node = this.querySelector(selector);
          if (node) node.textContent = value;
        };

        setText('[data-nutrition-calories]', String(macros.calories));
        setText('[data-nutrition-summary-protein]', `${macros.protein}g`);
        setText('[data-nutrition-summary-calories]', String(macros.calories));
        setText('[data-nutrition-summary-carbs]', `${macros.totalCarbs}g`);
        setText('[data-nutrition-summary-fat]', `${macros.totalFat}g`);
        setText('[data-nutrition-total-fat]', `${macros.totalFat}g`);
        setText('[data-nutrition-total-fat-dv]', this.dailyValue(macros.totalFat, 78));
        setText('[data-nutrition-cholesterol]', `${baseline.cholesterol}mg`);
        setText('[data-nutrition-cholesterol-dv]', '0%');
        setText('[data-nutrition-sodium]', `${baseline.sodium}mg`);
        setText('[data-nutrition-sodium-dv]', baseline.sodiumDv);
        setText('[data-nutrition-total-carbs]', `${macros.totalCarbs}g`);
        setText('[data-nutrition-total-carbs-dv]', this.dailyValue(macros.totalCarbs, 275));
        setText('[data-nutrition-protein]', `${macros.protein}g`);
        setText('[data-nutrition-protein-dv]', this.dailyValue(macros.protein, 50));
        setText('[data-nutrition-vitamin-d]', `${baseline.vitaminD}mcg`);
        setText('[data-nutrition-vitamin-d-dv]', '0%');
        setText('[data-nutrition-calcium]', `${baseline.calcium}mg`);
        setText('[data-nutrition-calcium-dv]', baseline.calciumDv);
        setText('[data-nutrition-iron]', `${baseline.iron}mg`);
        setText('[data-nutrition-iron-dv]', baseline.ironDv);
        setText('[data-nutrition-potassium]', `${baseline.potassium}mg`);
        setText('[data-nutrition-potassium-dv]', baseline.potassiumDv);

        const ingredientNames = items.map((tile) => tile.dataset.itemTitle).join(', ');
        const ingredientsNode = this.querySelector('[data-nutrition-ingredients-list]');
        if (ingredientsNode) {
          if (ingredientNames) {
            ingredientsNode.textContent = ingredientNames;
          } else {
            ingredientsNode.innerHTML = '<em>Select ingredients to see them here</em>';
          }
        }
      }

      updateCategoryBadges() {
        const counts = {};

        this.selectedIngredientKeys.forEach((key) => {
          const tile = this.tileMap.get(key);
          const category = tile?.dataset.itemCategory;
          if (!category) return;
          counts[category] = (counts[category] || 0) + 1;
        });

        this.categoryBadges.forEach((badge) => {
          const category = badge.dataset.categoryBadge;
          const count = counts[category] || 0;
          badge.textContent = `${count} added`;
          badge.hidden = count === 0;
        });
      }

      updateJar(items) {
        if (!this.jarTags || !this.jarCount) return;

        this.jarCount.textContent = `${items.length} ${this.ingredientsLabel}`;

        if (!items.length) {
          this.jarTags.classList.add('is-empty');
          this.jarTags.innerHTML = `<div class="kg-blend-builder__jar-empty">${this.jarEmptyText}</div>`;
          return;
        }

        this.jarTags.classList.remove('is-empty');
        this.jarTags.innerHTML = items
          .map((tile) => {
            const image = tile.querySelector('img');
            const title = tile.dataset.itemTitle || '';
            const proteinValue = Number(tile.dataset.protein);
            const proteinLabel = this.formatProteinGrams(proteinValue);
            const media = image
              ? `<img class="kg-blend-builder__jar-item-image" src="${image.currentSrc || image.src}" alt="" width="48" height="48" loading="lazy">`
              : '<span class="kg-blend-builder__jar-item-placeholder" aria-hidden="true"></span>';

            return `
              <div class="kg-blend-builder__jar-item">
                <div class="kg-blend-builder__jar-item-media">${media}</div>
                <div class="kg-blend-builder__jar-item-content">
                  <p class="kg-blend-builder__jar-item-title">${title}</p>
                </div>
                <span  style="display: none;" class="kg-blend-builder__jar-item-protein">${proteinLabel}</span>
                <button type="button" class="kg-blend-builder__jar-item-remove" data-remove-key="${tile.dataset.itemKey}" aria-label="Remove ${title}">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="none" aria-hidden="true">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M106.667 375.168C102.664 379.171 100.415 384.601 100.415 390.262C100.415 395.923 102.664 401.352 106.667 405.355C110.67 409.358 116.099 411.607 121.76 411.607C127.421 411.607 132.85 409.358 136.853 405.355L256 286.166L375.168 405.334C379.192 409.22 384.58 411.37 390.174 411.321C395.767 411.273 401.118 409.029 405.073 405.074C409.029 401.119 411.272 395.768 411.321 390.174C411.37 384.581 409.219 379.192 405.333 375.168L286.165 256L405.333 136.832C409.334 132.829 411.58 127.401 411.578 121.742C411.576 116.083 409.326 110.657 405.323 106.656C401.32 102.656 395.892 100.41 390.232 100.412C384.573 100.414 379.147 102.664 375.147 106.667L256 225.835L136.832 106.667C132.808 102.781 127.42 100.631 121.826 100.679C116.233 100.728 110.882 102.972 106.927 106.927C102.971 110.882 100.728 116.233 100.679 121.827C100.63 127.42 102.781 132.809 106.667 136.832L225.835 256L106.667 375.168Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
            `;
          })
          .join('');
      }

      getPreviousStep() {
        const stepKey = String(this.currentStep);

        if (stepKey === '1') return null;
        if (stepKey === 'premixes' || stepKey === '2') return 1;
        if (stepKey === '3') return 2;
        if (stepKey === '4') {
          return this.cameFromPremixes || this.selectedPremixKeys ? 'premixes' : 3;
        }

        return 1;
      }

      getCartActionButtons() {
        const buttons = [...(this.addToCartButtons || [])];
        if (this.primaryActionButton?.dataset.action === 'add-to-cart') {
          buttons.push(this.primaryActionButton);
        }
        return buttons;
      }

      updateActionButtons(items = this.getSelectedItems()) {
        const stepKey = String(this.currentStep);
        const isFirstStep = stepKey === '1';
        const isReviewStep = stepKey === '4';
        const canReview = items.length > 0;

        if (this.backButton) {
          this.backButton.disabled = isFirstStep;
          const previousStep = this.getPreviousStep();
          if (previousStep != null) {
            this.backButton.dataset.targetStep = String(previousStep);
          }
        }

        if (this.primaryActionButton) {
          const reviewLabel = this.primaryActionButton.querySelector('[data-review-label]');
          const craftLabel = this.primaryActionButton.querySelector('[data-craft-label]');
          const reviewIcon = this.primaryActionButton.querySelector('[data-review-icon]');
          const craftIcon = this.primaryActionButton.querySelector('[data-craft-icon]');

          if (isReviewStep) {
            this.primaryActionButton.dataset.action = 'add-to-cart';
            this.primaryActionButton.classList.remove('kg-blend-builder__btn-primary');
            this.primaryActionButton.classList.add('kg-blend-builder__btn-craft', 'kg-blend-builder__btn-craft--actions');
            if (reviewLabel) reviewLabel.hidden = true;
            if (craftLabel) craftLabel.hidden = false;
            if (reviewIcon) reviewIcon.hidden = true;
            if (craftIcon) craftIcon.hidden = false;
            this.primaryActionButton.disabled = !canReview || !this.dataset.variantId;
          } else {
            this.primaryActionButton.dataset.action = 'review-blend';
            this.primaryActionButton.classList.add('kg-blend-builder__btn-primary');
            this.primaryActionButton.classList.remove('kg-blend-builder__btn-craft', 'kg-blend-builder__btn-craft--actions');
            if (reviewLabel) reviewLabel.hidden = false;
            if (craftLabel) craftLabel.hidden = true;
            if (reviewIcon) reviewIcon.hidden = false;
            if (craftIcon) craftIcon.hidden = true;
            this.primaryActionButton.disabled = !canReview;
          }
        }

        this.addToCartButtons?.forEach((button) => {
          button.disabled = !canReview || !this.dataset.variantId;
        });

        this.progressSteps?.forEach((progressStep) => {
          if (Number(progressStep.dataset.progressStep) !== 4) return;
          progressStep.classList.toggle('is-locked', !canReview);
          progressStep.setAttribute('aria-disabled', canReview ? 'false' : 'true');
        });
      }

      updateUI() {
        const items = this.getSelectedItems();

        this.nextStepButtons?.forEach((button) => {
          const targetStep = Number(button.dataset.targetStep);
          if (button.hasAttribute('data-require-premix')) {
            button.disabled = !this.selectedPremixKeys;
          } else if (targetStep === 2) {
            button.disabled = this.selectedProteinKeys.size === 0;
          } else {
            button.disabled = false;
          }
        });

        this.updateActionButtons(items);
        this.updateJar(items);
        this.updateNutritionPanel(items);
        this.updateBlendPreview(items);
        this.updateCategoryBadges();
      }

      formatProteinGrams(value) {
        const amount = Number(value);
        if (!Number.isFinite(amount)) return '0g';

        const rounded = Math.round(amount * 100) / 100;
        if (Number.isInteger(rounded)) return `${rounded}g`;
        return `${rounded.toFixed(2)}g`;
      }

      updateBlendPreview(items) {
        if (!this.previewList) return;

        const macros = this.sumMacroNutrition(items);
        const blendName = this.blendNameInput?.value.trim() || this.defaultBlendName;

        if (this.previewName) this.previewName.textContent = blendName;
        if (this.previewProtein) this.previewProtein.textContent = this.formatProteinGrams(macros.protein);
        if (this.previewCalories) this.previewCalories.textContent = String(macros.calories);
        if (this.previewCarbs) this.previewCarbs.textContent = this.formatProteinGrams(macros.totalCarbs);
        if (this.previewFat) this.previewFat.textContent = this.formatProteinGrams(macros.totalFat);
        if (this.previewCount) this.previewCount.textContent = String(items.length);

        if (!items.length) {
          this.previewList.innerHTML = `<li class="kg-blend-builder__preview-list-empty">${this.jarEmptyText}</li>`;
          return;
        }

        this.previewList.innerHTML = items
          .map((tile) => {
            const title = tile.dataset.itemTitle || '';
            const protein = this.formatProteinGrams(tile.dataset.protein);
            const type = tile.dataset.itemType === 'protein' ? 'Base' : 'Boost';
            const image = tile.querySelector('img');
            const thumb = image
              ? `<img src="${image.currentSrc || image.src}" alt="" width="28" height="28" loading="lazy">`
              : '<span class="kg-blend-builder__preview-list-placeholder" aria-hidden="true"></span>';

            return `
              <li class="kg-blend-builder__preview-list-item">
                <span class="kg-blend-builder__preview-list-media">${thumb}</span>
                <span class="kg-blend-builder__preview-list-copy">
                  <span class="kg-blend-builder__preview-list-title">${title}</span>
                  <span class="kg-blend-builder__preview-list-type">${type}</span>
                </span>
                <span  style="display: none;" class="kg-blend-builder__preview-list-protein">${protein}</span>
              </li>
            `;
          })
          .join('');
      }

      goToStep(step, { syncPills = true } = {}) {
        this.currentStep = step;
        const stepKey = String(step);

        this.steps.forEach((panel) => {
          panel.hidden = String(panel.dataset.step) !== stepKey;
        });

        if (stepKey === 'premixes') {
          this.updateProgress('premixes');
          this.setActiveCategoryPill(this.premixesToggle);
          const pillBar = this.premixesToggle?.parentElement;
          if (pillBar && this.premixesToggle) {
            pillBar.scrollLeft = this.premixesToggle.offsetLeft;
          }
        } else {
          this.updateProgress(Number(step));
          if (syncPills) this.syncCategoryPillToStep(Number(step));
        }

        this.updateActionButtons();

        requestAnimationFrame(() => {
          const activeStep = this.querySelector(`[data-step="${stepKey}"]`);
          activeStep?.querySelectorAll('[data-accordion].is-open [data-accordion-panel]').forEach((panel) => {
            panel.hidden = false;
            panel.style.height = 'auto';
          });
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.announce(stepKey === 'premixes' ? 'Premixes' : `Step ${stepKey}`);
      }

      syncCategoryPillToStep(step) {
        if (step === 4) {
          if (this.cameFromPremixes || this.selectedPremixKeys) {
            this.setActiveCategoryPill(this.premixesToggle);
          }
          return;
        }

        let category = null;
        if (step === 1) category = 'proteins';
        if (step === 3) category = 'flavors';
        if (step === 2) {
          const openAccordion = this.querySelector('[data-step="2"] [data-accordion].is-open');
          category = openAccordion?.dataset.category || this.querySelector('[data-step="2"] [data-accordion]')?.dataset.category;
        }

        if (!category) return;

        const matchingPill = this.querySelector(`[data-category-nav="${category}"]`);
        if (matchingPill) this.setActiveCategoryPill(matchingPill);
      }

      updateProgress(step) {
        if (!this.progressSteps?.length) return;

        if (step === 'premixes') {
          this.progressSteps.forEach((progressStep) => {
            progressStep.classList.remove('is-active', 'is-complete');
          });
          return;
        }

        const stepNumber = Number(step);
        this.progressSteps.forEach((progressStep) => {
          const progressNumber = Number(progressStep.dataset.progressStep);
          progressStep.classList.remove('is-active', 'is-complete');

          if (progressNumber < stepNumber) {
            progressStep.classList.add('is-complete');
          } else if (progressNumber === stepNumber) {
            progressStep.classList.add('is-active');
          }
        });
      }

      announce(message) {
        if (this.statusMessage) {
          this.statusMessage.textContent = message;
        }
      }

      async addToCart(button) {
        const variantId = this.dataset.variantId;
        if (!variantId) return;

        const items = this.getSelectedItems();
        if (items.length === 0) return;

        this.getCartActionButtons().forEach((btn) => {
          btn.disabled = true;
          btn.classList.add('is-loading');
        });

        const ingredientList = items.map((tile) => tile.dataset.itemTitle).join(', ');
        const blendName = this.blendNameInput?.value.trim() || 'Custom Blend';
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };

        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('properties[Blend Name]', blendName);
        formData.append('properties[Ingredients]', ingredientList);
        formData.append('properties[Protein Base]', this.getSelectedProteinTitles());
        formData.append('properties[Nutrition Summary]', this.getNutritionSummary());
        this.appendLabelNutrition(formData, this.sumMacroNutrition(items));

        const sellingPlanId = this.sellingPlanSelect?.value;
        if (sellingPlanId) {
          formData.append('selling_plan', sellingPlanId);
        }

        if (cart?.getSectionsToRender) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id).join(',')
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }

        config.body = formData;

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add', config);
          const data = await response.json();

          if (data.status) {
            throw new Error(data.description || data.message || 'Unable to add to cart');
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'kg-blend-builder',
              productVariantId: variantId,
              cartData: data,
            });
          }

          if (cart?.renderContents) {
            cart.renderContents(data);
          } else if (cart?.open) {
            cart.open();
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }

          this.announce('Added to cart');
        } catch (error) {
          this.announce(error.message);
          window.alert(error.message);
        } finally {
          if (cart?.classList?.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
          this.getCartActionButtons().forEach((btn) => {
            btn.disabled = false;
            btn.classList.remove('is-loading');
          });
          this.updateUI();
        }
      }
    }
  );
}
